<script setup lang="ts">
import { computed, defineComponent, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { InputBox, PopupBasicHeader } from 'wepin-vue-component'
import InputLabel from './InputLabel.vue'
import {
  WrappedNetworkIcon,
  WrappedTokenListItem,
  BasicBottomButtons as BBB,
  NoticeText,
} from '@/components'
import { useBreakPointsClass, useInput, useKeyDown } from '@/composables'
import { defaultBreakPoints } from '@/const'
import {
  useAccountTokenAPIStore,
  useCoinStore,
  useWalletStore,
  useWalletAPIStore,
} from '@/stores'
import type { Coin, Token, Promisable } from '@/types'

const emits = defineEmits<{
  (event: 'close'): void
  (event: 'click-confirm', label: string): void
}>()

const { t } = useI18n()

const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const walletStore = useWalletStore()
const accountTokenAPIStore = useAccountTokenAPIStore()
const coinStore = useCoinStore()
const router = useRouter()
const { isError, inputValue: label, onInputLabel } = useInput()

const selectedCoin = computed(() => {
  const selectedCoinQuery = router.currentRoute.value.query.selectedCoin

  if (router.currentRoute.value.query.forToken === 'false') {
    // Coin 찾기: V2 Currency ID 또는 V1 ID로 검색
    const coin: Coin | undefined = coinStore.coinList!.find(
      (coin) =>
        coin.property?._v2CurrencyId === selectedCoinQuery ||
        coin.id.toString() === selectedCoinQuery,
    )
    return coin
  } else {
    // Token 찾기: V2 Currency ID 또는 V1 ID로 검색
    const token: Token | undefined = coinStore.tokenList!.find(
      (token) =>
        token.property?._v2CurrencyId === selectedCoinQuery ||
        token.id.toString() === selectedCoinQuery,
    )
    return token
  }
})

const selectedParentAccount = computed(() => {
  if (router.currentRoute.value.query.selectedParentAccount) {
    const account = walletStore.getAccount({
      accountId:
        router.currentRoute.value.query.selectedParentAccount.toString(),
    })
    return account
  }
  return null
})

const forToken = computed(() => {
  if (router.currentRoute.value.query.forToken === 'true') {
    return true
  } else {
    return false
  }
})

const onExit = () => {
  emits('close')
}

const onClickConfirm = async () => {
  if (isError.value) return

  // label이 비어있으면 기본값 생성: {CoinName}-01
  const finalLabel =
    label.value.trim() || selectedCoin.value?.name?.replace(/\s/g, '-') + '-01'

  if (forToken.value === false) {
    emits('click-confirm', finalLabel)
  } else {
    const walletId = router.currentRoute.value.params.walletId as string

    try {
      await accountTokenAPIStore.addToken({
        walletId: walletId,
        parentAccountId: selectedParentAccount.value?.id || '',
        currencyId:
          (selectedCoin.value?.property?._v2CurrencyId as string) ||
          selectedCoin.value?.id?.toString() ||
          '',
        label: finalLabel,
      })

      // V2: 토큰이 Account로 관리되므로 wallet 리스트 새로고침
      const walletAPIStore = useWalletAPIStore()
      await walletAPIStore.fetchWalletList({}, true)

      onExit()
    } catch (error) {
      throw error
    }
  }
}
useKeyDown({ confirm: onClickConfirm })
</script>
<script lang="ts">
export const createInputLabel = (params: {
  onClickConfirm: (label: string) => Promisable<void>
}) => {
  return defineComponent<typeof InputLabel>((props) => {
    return () =>
      h(InputLabel, {
        'onClick-confirm': params.onClickConfirm,
        ...props,
      })
  })
}
</script>
<template>
  <PopupBasicHeader :title="t('create_account.add_account')" :exit="onExit" />
  <div class="input-label" :class="[breakPointsClass]">
    <div class="notice-text">
      {{ t('create_account.input_name_title') }}
      <span v-text="t('create_account.input_name_sub')" />
    </div>
    <div
      v-if="selectedParentAccount"
      class="networkWrapper"
      :class="forToken ? 'for-token' : ''"
    >
      <span>{{ t('send.network') }}</span>
      <WrappedNetworkIcon
        :token-src="selectedParentAccount?.coin?.iconUrl"
        size="custom"
        custom-size="20px"
      />
      <span>{{ selectedParentAccount?.coin?.name }}</span>
    </div>
    <WrappedTokenListItem
      class="token-list-item"
      :ticker="selectedCoin?.name"
      :symbol="selectedCoin?.symbol"
      :ticker-src="selectedCoin?.iconUrl"
      :network-src="selectedParentAccount?.coin?.iconUrl"
    />
    <InputBox
      width="100%"
      :placeholder="t('create_account.name_placeholder')"
      :clear="true"
      :model-value="label"
      :error="isError"
      maxlength="14"
      @update:model-value="onInputLabel"
    />
    <NoticeText class="notice-wrapper" :is-error="isError">
      {{ t('create_account.input_name_caution') }}
    </NoticeText>
    <BBB :disabled="isError" @confirm="onClickConfirm" @cancel="onExit" />
  </div>
</template>

<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';

.input-label {
  padding: 0 30px;
  max-width: 460px;
  .notice-text {
    @include typeface('Title_18');
    & > span {
      display: block;
      margin-top: 10px;
      @include typeface('Paragraph_16_r');
    }
  }

  .networkWrapper {
    display: flex;
    background-color: rgba(98, 126, 234, 0.1);
    border-radius: 8px;
    gap: 5px;
    height: 50px;
    align-items: center;
    padding: 14px 20px;
    margin-top: 20px;
    @include typeface('Paragraph_14_r');
    color: var(--grey_111);
    & > span:last-of-type {
      font-weight: 600;
    }
  }
  .token-list-item {
    padding: {
      left: 0;
      right: 0;
    }
  }
  .notice-wrapper {
    background-color: transparent;
    margin-top: 10px;
    margin-bottom: 20px;
    padding: initial;
  }

  .buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 30px;
  }

  .for-token {
    background-color: rgba(240, 185, 11, 0.1) !important;
  }
}

.input-label.mobile {
  max-width: 100%;
  padding: 0 20px;
  width: 100%;
  max-width: none;
}
</style>
